import { useDispatch, useSelector } from 'react-redux';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useConfirmModal from '../useConfirmModal';
import { RootState } from '../../store/store';
import {
    closeAddProductModal,
    closeEditProductModal,
} from '../../store/slices/sale/creditNoteSaleSlice';
import { clearSaleProducts, setSaleProducts, updateSaleTotals, updateSaleTotalsView } from '../../store/slices/sale/saleSlice';
import { useSale } from './useSale';
import CreditNoteSaleService from '../../services/sale/CreditNoteSaleService';
import useApiErrorHandler from '../useApiErrorHandler';
import { updateReturnInfoList } from '../../store/slices/sale/creditNoteSaleLookupSlice';

export const useCreditNoteSale = () => {
    const dispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const confirm = useConfirmModal();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const {
        handlerAddProduct,
        handlerUpdateProduct,
        handlerDeleteProduct,
        handlerSelectProduct,
        form,
    } = useSale();

    const { showAddProductModal, showEditProductModal } = useSelector(
        (state: RootState) => state.creditNoteSale
    );

    const handlerAddCreditNoteProduct = async (product: any) => {
        // console.log(product);
        try {
            showLoadingIndicator();
            const response = await CreditNoteSaleService.addProduct(product);
            // console.log(response);
            if (response.statusCode === 201) {
                const { title, message, type, data } = response;
                const { product: savedProduct, totals, totalsView, allReturnInfo } = data;
                console.log(allReturnInfo)

                handlerAddProduct(savedProduct);
                dispatch(updateSaleTotals(totals));
                dispatch(updateSaleTotalsView(totalsView));
                dispatch(updateReturnInfoList(allReturnInfo));
                dispatch(closeAddProductModal());
                showAlert(type, title, message);
            } else {
                showAlert('error', 'Error', 'No se pudo agregar el producto.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerUpdateCreditNoteProduct = async (product: any) => {
        // console.log(product)
        try {
            showLoadingIndicator();
            const response = await CreditNoteSaleService.updateProduct(product);
            // console.log(response);
            if (response.statusCode === 200) {
                const { title, message, type, data } = response;
                const { product: updatedProduct, totals, totalsView, allReturnInfo } = data;

                handlerUpdateProduct(updatedProduct);
                dispatch(updateSaleTotals(totals));
                dispatch(updateSaleTotalsView(totalsView));
                dispatch(updateReturnInfoList(allReturnInfo));
                dispatch(closeEditProductModal());
                showAlert(type, title, message);
            } else {
                showAlert('error', 'Error', 'No se pudo actualizar el producto.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerDeleteCreditNoteProduct = async (id: number) => {
        confirm({
            title: '¿Eliminar Producto?',
            message: '¿Estás seguro que deseas eliminar el producto?',
            icon: 'warning',
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            confirmArgs: id,
            onConfirm: async (id) => {
                try {
                    showLoadingIndicator();
                    const response = await CreditNoteSaleService.deleteProduct(id);
                    // console.log(response);
                    if (response.statusCode === 200) {
                        const { title, message, type, data } = response;
                        const { totals, totalsView, allReturnInfo } = data;

                        handlerDeleteProduct(id);                         // Elimina del estado local
                        dispatch(updateSaleTotals(totals));
                        dispatch(updateSaleTotalsView(totalsView));
                        dispatch(updateReturnInfoList(allReturnInfo));   // ✅ Actualiza la lista de balances
                        showAlert(type, title, message);            // Muestra modal de éxito
                    } else {
                        showAlert('error', 'Error', 'No se pudo eliminar el producto.');
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    const handlerGetProductById = async (detailId: number) => {
        try {
            showLoadingIndicator();
            const response = await CreditNoteSaleService.getProductById(detailId);
            // console.log(response)
            if (response.statusCode === 200) {
                handlerSelectProduct(response.data);
                return response.data;
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el producto.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerClearAllProducts = async (creditNoteId: number) => {
        confirm({
            title: '¿Eliminar todos los productos?',
            message: 'Esta acción eliminará todos los productos de la nota de crédito. ¿Deseas continuar?',
            icon: 'warning',
            confirmText: 'Sí, eliminar todo',
            cancelText: 'Cancelar',
            confirmArgs: creditNoteId,
            onConfirm: async (id) => {
                try {
                    showLoadingIndicator();
                    const response = await CreditNoteSaleService.clearAllProducts(id);
                    if (response.statusCode === 200) {
                        const { data: { totals, totalsView, allReturnInfo, }, title, message, type } = response;
                        dispatch(updateSaleTotals(totals));
                        dispatch(updateSaleTotalsView(totalsView));
                        dispatch(updateReturnInfoList(allReturnInfo));
                        showAlert(type, title, message);
                        dispatch(clearSaleProducts())
                    } else {
                        showAlert('error', 'Error', 'No se pudo limpiar la nota de crédito.');
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };


    const creditFiscalId = form.creditFiscalId;

    const handlerReturnAllProducts = async (creditNoteId: number) => {
        if (!creditFiscalId) {
            showAlert('error', 'Error', 'No se ha establecido la venta de crédito fiscal asociada.');
            return;
        }

        confirm({
            title: '¿Devolver todos los productos?',
            message: 'Esto agregará automáticamente todos los productos vendidos aún no devueltos.',
            icon: 'info',
            confirmText: 'Devolver Todo',
            cancelText: 'Cancelar',
            confirmArgs: creditNoteId,
            onConfirm: async (id: number) => {
                try {
                    const response = await CreditNoteSaleService.returnAllProducts(id, creditFiscalId);

                    const {
                        data: { totals, totalsView, allReturnInfo, products }, // <-- cambia `product` por `products`
                        title,
                        message,
                        type,
                    } = response;

                    // ✅ Establecer lista completa de productos
                    dispatch(setSaleProducts(products));
                    dispatch(updateSaleTotals(totals));
                    dispatch(updateSaleTotalsView(totalsView));
                    dispatch(updateReturnInfoList(allReturnInfo));
                    showAlert(type, title, message);
                } catch (error: any) {
                    handle(error);
                }
            },
        });
    };

    return {
        handlerAddCreditNoteProduct,
        handlerUpdateCreditNoteProduct,
        handlerDeleteCreditNoteProduct,
        handlerGetProductById,
        showAddProductModal,
        showEditProductModal,
        handlerClearAllProducts,
        handlerReturnAllProducts,
    };
};
